import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plant,
  Buildings,
  Gear,
  Factory,
  ShoppingBag,
  Flask,
  Lightning,
  Cpu,
  Heartbeat,
  Truck,
  ChartLineUp,
  MagnifyingGlass,
  UsersThree,
  Handshake,
  MapPinLine,
  Package,
  TreeStructure,
  Globe,
  ChatCircleText,
  ShieldCheck,
  FileText,
  ArrowRight,
  Plus,
  Minus,
  Lock,
  Scales,
  Star,
} from "@phosphor-icons/react";
import SEO, { SITE_URL } from "@/components/site/SEO";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import PartnershipForm from "@/components/site/PartnershipForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1605745341112-85968b19335b?auto=format&fit=crop&w=1920&q=80";

const INDUSTRIES = [
  {
    icon: Plant,
    slug: "food-agriculture",
    partnerType: "Supplier",
    name: "Food & Agriculture",
    desc: "Connecting producers, exporters and buyers across the global food and agricultural value chain.",
    opportunities: "Export/import of grains, pulses, oils, fresh produce and processed foods across MENA, Europe and Asia.",
    partnerships: "Producers · Exporters · Distributors · Wholesale buyers",
    services: "Supplier sourcing · Quality verification · Trade facilitation",
  },
  {
    icon: Buildings,
    slug: "construction-materials",
    partnerType: "Supplier",
    name: "Construction Materials",
    desc: "Facilitating supply of building materials for infrastructure and development projects.",
    opportunities: "Cement, steel, aggregates, ceramics and finishing materials for regional mega-projects.",
    partnerships: "Manufacturers · Contractors · Project developers",
    services: "Procurement · Manufacturer sourcing · Logistics coordination",
  },
  {
    icon: Gear,
    slug: "industrial-equipment",
    partnerType: "Supplier",
    name: "Industrial Equipment & Machinery",
    desc: "Sourcing reliable industrial machinery and equipment for manufacturers and operators.",
    opportunities: "Production lines, heavy machinery, spare parts and turnkey industrial systems.",
    partnerships: "OEMs · Distributors · Industrial buyers",
    services: "Technical sourcing · Supplier verification · Negotiation support",
  },
  {
    icon: Factory,
    slug: "manufacturing",
    partnerType: "Manufacturer",
    name: "Manufacturing",
    desc: "Bridging manufacturers with international buyers and contract-production opportunities.",
    opportunities: "OEM/ODM manufacturing, private label and contract production partnerships.",
    partnerships: "Manufacturers · Brands · Importers",
    services: "Business matchmaking · Capacity assessment · Representation",
  },
  {
    icon: ShoppingBag,
    slug: "consumer-goods",
    partnerType: "Distributor",
    name: "Consumer Goods",
    desc: "Enabling brands and retailers to expand into new consumer markets.",
    opportunities: "FMCG, household, personal-care and lifestyle products for retail and distribution.",
    partnerships: "Brands · Retailers · Distributors",
    services: "Market entry · Distributor matching · Commercial brokerage",
  },
  {
    icon: Flask,
    slug: "chemicals",
    partnerType: "Supplier",
    name: "Chemicals & Raw Materials",
    desc: "Connecting suppliers and industrial buyers of chemicals and base materials.",
    opportunities: "Industrial chemicals, polymers, petrochemicals and raw material inputs.",
    partnerships: "Producers · Traders · Industrial consumers",
    services: "Sourcing · Compliance guidance · Supply agreements",
  },
  {
    icon: Lightning,
    slug: "energy",
    partnerType: "Strategic Partner",
    name: "Energy",
    desc: "Supporting commercial relationships across conventional and renewable energy.",
    opportunities: "Energy equipment, solar solutions, commodities and project partnerships.",
    partnerships: "Suppliers · Investors · Project developers",
    services: "Investment introductions · Partner sourcing · Market entry",
  },
  {
    icon: Cpu,
    slug: "technology",
    partnerType: "Technology Partner",
    name: "Technology & Digital Solutions",
    desc: "Linking technology providers with regional partners and enterprise buyers.",
    opportunities: "Hardware, software, IoT and digital transformation solutions.",
    partnerships: "Vendors · System integrators · Enterprise clients",
    services: "Strategic partnerships · Representation · Business development",
  },
  {
    icon: Heartbeat,
    slug: "healthcare",
    partnerType: "Medical Supplier",
    name: "Healthcare & Medical Supplies",
    desc: "Facilitating trusted sourcing of medical products and healthcare equipment.",
    opportunities: "Medical devices, consumables, pharmaceuticals and hospital equipment.",
    partnerships: "Manufacturers · Distributors · Healthcare institutions",
    services: "Supplier verification · Procurement · Regulatory-aware sourcing",
  },
  {
    icon: Truck,
    slug: "logistics",
    partnerType: "Logistics Partner",
    name: "Logistics & Supply Chain",
    desc: "Optimising cross-border movement of goods through reliable logistics partners.",
    opportunities: "Freight, warehousing, customs and end-to-end supply-chain solutions.",
    partnerships: "Logistics providers · Freight forwarders · Traders",
    services: "Partner identification · Coordination · Trade facilitation",
  },
  {
    icon: ChartLineUp,
    slug: "investment",
    partnerType: "Investor",
    name: "Investment & Business Development",
    desc: "Introducing investors to vetted commercial and growth opportunities.",
    opportunities: "Joint ventures, expansion projects and strategic investment mandates.",
    partnerships: "Investors · Founders · Corporate partners",
    services: "Investment introductions · Due diligence support · Deal facilitation",
  },
];

const CAPABILITIES = [
  { icon: MagnifyingGlass, t: "Supplier Identification" },
  { icon: Factory, t: "Manufacturer Sourcing" },
  { icon: UsersThree, t: "Business Matchmaking" },
  { icon: Handshake, t: "Commercial Brokerage" },
  { icon: MapPinLine, t: "Market Entry Support" },
  { icon: Package, t: "Procurement Assistance" },
  { icon: TreeStructure, t: "Strategic Partnerships" },
  { icon: ChartLineUp, t: "Investment Introductions" },
  { icon: Globe, t: "International Representation" },
];

const PROCESS = [
  { icon: ChatCircleText, t: "Understanding Client Requirements" },
  { icon: MagnifyingGlass, t: "Market Research" },
  { icon: UsersThree, t: "Partner Identification" },
  { icon: ShieldCheck, t: "Due Diligence" },
  { icon: Handshake, t: "Negotiation Support" },
  { icon: FileText, t: "Contract Coordination" },
  { icon: TreeStructure, t: "Long-Term Business Development" },
];

const WHY = [
  { icon: Globe, t: "International Network", d: "Direct access to vetted partners across multiple continents and sectors." },
  { icon: Handshake, t: "Trusted Relationships", d: "Long-standing commercial relationships built on reliability and results." },
  { icon: ChatCircleText, t: "Professional Communication", d: "Clear, responsive and multilingual coordination at every stage." },
  { icon: Lock, t: "Confidentiality", d: "NDA-backed handling of sensitive commercial information and identities." },
  { icon: Scales, t: "Compliance", d: "Business conducted to international standards, ethics and due diligence." },
  { icon: Star, t: "Market Expertise", d: "Deep understanding of regional dynamics, pricing and opportunity." },
  { icon: TreeStructure, t: "Long-Term Value Creation", d: "Partnerships engineered to compound value well beyond the first deal." },
];

export default function Industries() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openCard, setOpenCard] = useState(0);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const goContact = () => navigate("/", { state: { scrollTo: "contact" } });

  const enquire = (industryName, partnerType) => {
    setSelectedIndustry(industryName);
    setSelectedType(partnerType || "");
    setPartnerOpen(true);
  };

  // Deep link: /industries#slug opens & scrolls to the matching sector
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (!hash) return;
    const idx = INDUSTRIES.findIndex((it) => it.slug === hash);
    if (idx >= 0) {
      setOpenCard(idx);
      const t = setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Industries Served by Altin Link",
    description:
      "Commercial brokerage and business consulting expertise across food & agriculture, construction, manufacturing, energy, technology, healthcare, logistics and more.",
    itemListElement: INDUSTRIES.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
    })),
  };

  return (
    <div data-testid="industries-page" className="min-h-screen bg-altin-bg">
      <SEO
        title="Industries We Serve"
        description="Altin Link delivers strategic commercial solutions across food & agriculture, construction, manufacturing, industrial equipment, consumer goods, chemicals, energy, technology, healthcare, logistics and investment — connecting international buyers, suppliers, manufacturers and investors."
        path="/industries"
        schema={schema}
      />
      <Navbar />

      {/* HERO */}
      <section
        data-testid="ind-hero"
        className="relative min-h-[72vh] flex items-center bg-altin-ink overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-altin-ink via-altin-ink/70 to-altin-ink/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="gold-divider" />
              <span className="overline">Sectors & Expertise</span>
            </div>
            <h1
              data-testid="ind-hero-title"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white"
            >
              Industries We{" "}
              <span className="italic text-altin-gold">Serve</span>
            </h1>
            <p className="mt-8 text-lg md:text-2xl text-white/75 font-light max-w-2xl leading-relaxed">
              Delivering strategic commercial solutions across diverse
              industries through trusted international partnerships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section data-testid="ind-intro" className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-2xl md:text-3xl leading-relaxed text-altin-ink tracking-tight"
          >
            Altin Link supports companies in identifying opportunities, sourcing
            reliable partners, facilitating international trade, and developing
            long-term commercial relationships — across a wide spectrum of
            industries and markets.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 text-altin-muted leading-relaxed"
          >
            Whether you are a manufacturer seeking new markets, a buyer sourcing
            trusted suppliers, or an investor exploring opportunities, our
            sector expertise and international network turn ambition into
            durable commercial partnerships.
          </motion.p>
        </div>
      </section>

      {/* INDUSTRY CARDS (interactive accordion) */}
      <section
        id="industry-cards"
        data-testid="ind-cards-section"
        className="py-20 md:py-28 bg-altin-bg"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-divider" />
            <span className="overline">Our Industries</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-altin-ink tracking-tight mb-14">
            Eleven sectors,{" "}
            <span className="italic text-altin-gold">one trusted partner</span>.
          </h2>

          <div className="border-t border-altin-border">
            {INDUSTRIES.map((it, i) => {
              const Icon = it.icon;
              const open = openCard === i;
              return (
                <div
                  key={it.name}
                  id={it.slug}
                  data-testid={`ind-card-${i}`}
                  className="border-b border-altin-border scroll-mt-28"
                >
                  <button
                    onClick={() => setOpenCard(open ? -1 : i)}
                    data-testid={`ind-card-toggle-${i}`}
                    className="w-full flex items-center gap-5 py-7 text-left group"
                    aria-expanded={open}
                  >
                    <span
                      className={`w-12 h-12 grid place-items-center border shrink-0 transition-colors ${
                        open
                          ? "bg-altin-gold border-altin-gold text-white"
                          : "border-altin-gold/50 text-altin-gold group-hover:border-altin-gold"
                      }`}
                    >
                      <Icon size={22} weight="light" />
                    </span>
                    <span className="flex-1">
                      <span className="text-[10px] tracking-[0.25em] uppercase text-altin-gold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="block font-serif-display text-2xl md:text-3xl text-altin-ink tracking-tight">
                        {it.name}
                      </span>
                    </span>
                    <span className="text-altin-gold shrink-0">
                      {open ? <Minus size={22} weight="light" /> : <Plus size={22} weight="light" />}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-9 pl-0 md:pl-16 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                          <p className="md:col-span-2 text-altin-muted leading-relaxed">
                            {it.desc}
                          </p>
                          <Detail label="International Market Opportunities" value={it.opportunities} />
                          <Detail label="Partnerships Supported" value={it.partnerships} />
                          <Detail label="Commercial Services Provided" value={it.services} full />
                          <div className="md:col-span-2 pt-2">
                            <button
                              type="button"
                              data-testid={`ind-enquire-${i}`}
                              onClick={() => enquire(it.name, it.partnerType)}
                              className="group inline-flex items-center gap-3 bg-altin-ink text-white px-6 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-altin-gold transition-colors duration-300"
                            >
                              Enquire About This Sector
                              <ArrowRight weight="light" className="transition-transform group-hover:translate-x-1" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR CAPABILITIES */}
      <section
        data-testid="ind-capabilities-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-divider" />
              <span className="overline">Our Capabilities</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-altin-ink tracking-tight">
              End-to-end commercial{" "}
              <span className="italic text-altin-gold">support</span>.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-altin-border border border-altin-border">
            {CAPABILITIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.t}
                  data-testid={`ind-capability-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                  className="group bg-white p-8 flex items-center gap-5 hover:bg-altin-bg transition-colors"
                >
                  <div className="w-12 h-12 grid place-items-center border border-altin-gold text-altin-gold shrink-0 group-hover:bg-altin-gold group-hover:text-white transition-colors">
                    <Icon size={22} weight="light" />
                  </div>
                  <span className="font-serif-display text-xl text-altin-ink tracking-tight">
                    {c.t}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WORKING PROCESS */}
      <section
        data-testid="ind-process-section"
        className="py-24 md:py-32 bg-altin-bg"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-divider" />
              <span className="overline">Our Working Process</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-altin-ink tracking-tight">
              From brief to{" "}
              <span className="italic text-altin-gold">lasting business</span>.
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {PROCESS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.t}
                  data-testid={`ind-process-${i}`}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.06 }}
                  className="flex items-start gap-5 py-6 border-b border-altin-border"
                >
                  <span className="font-serif-display text-3xl text-altin-gold/40 w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="w-11 h-11 grid place-items-center border border-altin-gold/50 text-altin-gold shrink-0">
                    <Icon size={20} weight="light" />
                  </div>
                  <h3 className="font-serif-display text-xl text-altin-ink tracking-tight pt-1.5">
                    {s.t}
                  </h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section
        data-testid="ind-why-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-divider" />
              <span className="overline">Why Clients Choose Altin Link</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-altin-ink tracking-tight">
              The Altin Link{" "}
              <span className="italic text-altin-gold">advantage</span>.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-altin-border border border-altin-border">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.t}
                  data-testid={`ind-why-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                  className="group bg-white p-8 hover:bg-altin-bg transition-colors"
                >
                  <div className="w-12 h-12 grid place-items-center border border-altin-gold text-altin-gold mb-6 group-hover:bg-altin-gold group-hover:text-white transition-colors">
                    <Icon size={22} weight="light" />
                  </div>
                  <h3 className="font-serif-display text-xl text-altin-ink tracking-tight">
                    {w.t}
                  </h3>
                  <p className="mt-3 text-sm text-altin-muted leading-relaxed">
                    {w.d}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="ind-cta-section"
        className="relative py-28 md:py-36 bg-altin-ink overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-4xl md:text-6xl leading-[1.03] text-white tracking-tight"
          >
            Let&apos;s Build Successful International{" "}
            <span className="italic text-altin-gold">Business</span> Together.
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-4"
          >
            <button
              type="button"
              data-testid="ind-cta-contact"
              onClick={goContact}
              className="group inline-flex items-center gap-3 bg-altin-gold text-altin-ink px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-altin-goldHover transition-colors duration-300"
            >
              Contact Us
              <ArrowRight weight="light" className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              data-testid="ind-cta-consultation"
              onClick={goContact}
              className="group inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:border-altin-gold hover:text-altin-gold transition-colors duration-300"
            >
              Request Consultation
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <PartnershipForm
        open={partnerOpen}
        onClose={() => setPartnerOpen(false)}
        initialIndustry={selectedIndustry}
        initialPartnershipType={selectedType}
      />
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-altin-gold mb-2">
        {label}
      </div>
      <p className="text-sm text-altin-ink leading-relaxed">{value}</p>
    </div>
  );
}
